class TeamActivityAPIService {
  constructor() {
    this.baseURL = process.env.REACT_APP_BLOG_API_BASE_URL;
    this.imageBaseURL = process.env.REACT_APP_IMAGE_BASE_URL;
    this.cmsBaseURL = process.env.REACT_APP_API_URL;
  }

  // Helper function to check if a file is a video
  isVideoFile(url) {
    if (!url || typeof url !== 'string') return false;
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'];
    const urlLower = url.toLowerCase();
    return videoExtensions.some(ext => urlLower.includes(ext));
  }

  // Helper function to extract image/video URL from different possible structures
  extractMediaUrl(activityData) {
    // Try different possible image field names and structures
    const possibleMediaFields = [
      'postImages',
      'images',
      'image',
      'featuredImage',
      'featured_image',
      'thumbnail',
      'cover_image',
      'banner',
      'photo',
      'picture',
      'media'
    ];

    for (const field of possibleMediaFields) {
      const mediaField = activityData[field];
      
      if (typeof mediaField === 'string' && mediaField.trim() !== '') {
        // Direct URL string - check if it needs a base URL
        let mediaUrl = mediaField.trim();
        
        // If it's a relative URL, prepend base URL
        if (!mediaUrl.startsWith('http') && !mediaUrl.startsWith('data:')) {
          // Check if it starts with /
          if (mediaUrl.startsWith('/')) {
            // Use the correct image base URL for paths starting with /
            mediaUrl = `${this.imageBaseURL}${mediaUrl}`;
          } else {
            // If it doesn't start with /, add both base URL and /
            mediaUrl = `${this.imageBaseURL}/${mediaUrl}`;
          }
        }
        
        // Check if this is a video file
        if (this.isVideoFile(mediaUrl)) {
          return {
            type: 'video',
            url: mediaUrl,
            isVideo: true
          };
        }
        
        return mediaUrl;
      } else if (Array.isArray(mediaField) && mediaField.length > 0) {
        // Array of media - take the first one (index 0)
        const firstMedia = mediaField[0];
        if (typeof firstMedia === 'string') {
          let mediaUrl = firstMedia.trim();
          
          // Handle relative URLs
          if (!mediaUrl.startsWith('http') && !mediaUrl.startsWith('data:')) {
            if (mediaUrl.startsWith('/')) {
              mediaUrl = `${this.imageBaseURL}${mediaUrl}`;
            } else {
              mediaUrl = `${this.imageBaseURL}/${mediaUrl}`;
            }
          }
          
          // Check if this is a video file
          if (this.isVideoFile(mediaUrl)) {
            return {
              type: 'video',
              url: mediaUrl,
              isVideo: true
            };
          }
          
          return mediaUrl;
        } else if (firstMedia && firstMedia.url) {
          let mediaUrl = firstMedia.url.trim();
          
          // Handle relative URLs
          if (!mediaUrl.startsWith('http') && !mediaUrl.startsWith('data:')) {
            if (mediaUrl.startsWith('/')) {
              mediaUrl = `${this.imageBaseURL}${mediaUrl}`;
            } else {
              mediaUrl = `${this.imageBaseURL}/${mediaUrl}`;
            }
          }
          
          // Check if this is a video file
          if (this.isVideoFile(mediaUrl)) {
            return {
              type: 'video',
              url: mediaUrl,
              isVideo: true
            };
          }
          
          return mediaUrl;
        }
      } else if (mediaField && typeof mediaField === 'object' && mediaField.url) {
        // Object with url property
        let mediaUrl = mediaField.url.trim();
        
        // Handle relative URLs
        if (!mediaUrl.startsWith('http') && !mediaUrl.startsWith('data:')) {
          if (mediaUrl.startsWith('/')) {
            mediaUrl = `${this.imageBaseURL}${mediaUrl}`;
          } else {
            mediaUrl = `${this.imageBaseURL}/${mediaUrl}`;
          }
        }
        
        // Check if this is a video file
        if (this.isVideoFile(mediaUrl)) {
          return {
            type: 'video',
            url: mediaUrl,
            isVideo: true
          };
        }
        
        return mediaUrl;
      }
    }

    return null;
  }

  // Fetch team activity posts with authentication
  async fetchTeamActivityPosts(page = 1, teamId = 1) {
    try {
      // Use the backend CMS API endpoint for custom queries
      const url = `${this.cmsBaseURL}/external-data/insight-data`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: `/teamactivity_feed_post?page=${page}&hash=teamactivity&team_id=${teamId}`
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const data = result.data;
      
      // Transform the API response to match the existing structure
      return this.transformTeamActivityData(data);
    } catch (error) {
      console.error('Error fetching team activity posts:', error);
      console.error('Full error details:', error.message);
      
      // Throw error instead of returning empty array
      throw new Error('Failed to fetch team activity data from API');
    }
  }

  // Fetch a single team activity post by ID
  async fetchTeamActivityPostById(id) {
    try {
      // Add timestamp to prevent caching issues
      const timestamp = new Date().getTime();
      
      // Use the backend CMS API endpoint for custom queries
      const url = `${this.cmsBaseURL}/external-data/insight-data`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: `/feed_one_post?id=${id}&_t=${timestamp}`
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const data = result.data;
      
      // Transform the single post data
      return this.transformSingleTeamActivityData(data);
    } catch (error) {
      console.error('Error fetching single team activity post:', error);
      console.error('Full error details:', error.message);
      throw error;
    }
  }

  // Transform API data to match existing component structure
  transformTeamActivityData(apiData) {
    // Handle different possible API response structures
    let activityArray = [];
    
    if (Array.isArray(apiData)) {
      // Direct array response
      activityArray = apiData;
    } else if (apiData && Array.isArray(apiData.data)) {
      // Structure with data property
      activityArray = apiData.data;
    } else if (apiData && Array.isArray(apiData.results)) {
      // API with results property
      activityArray = apiData.results;
    } else if (apiData && Array.isArray(apiData.items)) {
      // API with items property
      activityArray = apiData.items;
    } else if (apiData && Array.isArray(apiData.posts)) {
      // API with posts property
      activityArray = apiData.posts;
    } else {
      console.warn('Unexpected Team Activity API response structure:', apiData);
      return [];
    }

    return activityArray.map((activity, index) => {
      // Handle different possible activity object structures
      const activityData = activity.attributes || activity;
      
      // Extract media URL (image or video)
      const extractedMedia = this.extractMediaUrl(activityData);
      
      // Handle video objects vs regular image URLs
      let image, isVideo = false, videoUrl = null;
      
      if (extractedMedia && typeof extractedMedia === 'object' && extractedMedia.isVideo) {
        // This is a video
        image = "/png/placeholder.png"; // Use placeholder image for video
        isVideo = true;
        videoUrl = extractedMedia.url;
      } else {
        // This is a regular image
        image = extractedMedia || "/png/placeholder.png";
      }
      
      return {
        id: activity.id || activityData.id || `activity-${index}`,
        image: image,
        isVideo: isVideo,
        videoUrl: videoUrl,
        tag: activityData.category || activityData.tag || activityData.type || "Team Activity",
        title: activityData.title || activityData.post_title,
        desc: activityData.post_desc || activityData.description || activityData.summary || activityData.excerpt || "",
        post_desc: activityData.post_desc || activityData.description || activityData.content || activityData.body || "",
        content: activityData.content || activityData.post_desc || activityData.description || "",
        overlay: true,
        publishedAt: activityData.publishedAt || activityData.published_at || activityData.created_at || new Date().toISOString(),
        slug: activityData.slug || `activity-${activity.id || index}`,
        author: (typeof (activityData.fullName || activityData.author || activityData.posted_by) === 'string' 
                 ? (activityData.fullName || activityData.author || activityData.posted_by) 
                 : (activityData.fullName?.fullName || activityData.fullName?.name || "BA Systems Team")) || "BA Systems Team",
        timeAgo: this.calculateTimeAgo(activityData.publishedAt || activityData.published_at || activityData.created_at),
      };
    });
  }

  // Transform single team activity data
  transformSingleTeamActivityData(apiData) {
    if (!apiData) {
      return null;
    }

    // Handle the specific API response structure
    let activity = apiData;
    
    // The API returns: { responseCode: 200, success: true, data: [postObject] }
    if (apiData.data && Array.isArray(apiData.data) && apiData.data.length > 0) {
      activity = apiData.data[0];
    } else if (apiData.data && !Array.isArray(apiData.data)) {
      activity = apiData.data;
    } else if (apiData.responseCode === 200 && apiData.success && !apiData.data) {
      // Direct response object
      activity = apiData;
    }

    // The activity data should be used directly (no nested attributes in this API)
    const activityData = activity;
    
    const extractedMedia = this.extractMediaUrl(activityData);

    // Handle video objects vs regular image URLs
    let image, isVideo = false, videoUrl = null;
    
    if (extractedMedia && typeof extractedMedia === 'object' && extractedMedia.isVideo) {
      // This is a video
      image = "/png/placeholder.png"; // Use placeholder image for video
      isVideo = true;
      videoUrl = extractedMedia.url;
    } else {
      // This is a regular image
      image = extractedMedia || "/png/placeholder.png";
    }

    const hashtags = this.extractHashtags(activityData.post_desc || activityData.description || "");
    
    const selectedDesc = activityData.post_desc || activityData.description || activityData.content || activityData.body || "";
    
    const transformedPost = {
      id: activity.id || activityData.id,
      image: image,
      isVideo: isVideo,
      videoUrl: videoUrl,
      tag: activityData.category || activityData.tag || activityData.type || "Team Activity",
      title: activityData.title || activityData.post_title,
      desc: selectedDesc,
      post_desc: selectedDesc,
      content: selectedDesc,
      summary: activityData.summary || activityData.caption || "",
      author: activityData.fullName || activityData.author || activityData.posted_by || "BA Systems Team",
      overlay: true,
      publishedAt: activityData.publishedAt || activityData.published_at || activityData.created_at || activityData.updated_at,
      slug: activityData.slug || `activity-${activity.id}`,
      timeAgo: this.calculateTimeAgo(activityData.publishedAt || activityData.published_at || activityData.created_at || activityData.updated_at),
      hashtags: hashtags,
      likeCount: activityData.like || 0,
      commentCount: activityData.comments || 0
    };
    
    return transformedPost;
  }

  // Helper method to extract hashtags from text
  extractHashtags(text) {
    if (!text || typeof text !== 'string') return [];
    
    // Match hashtags (# followed by word characters, including unicode)
    const hashtagRegex = /#[\w\u0980-\u09FF]+/g;
    const hashtags = text.match(hashtagRegex) || [];
    
    // Remove duplicates and return
    return [...new Set(hashtags)];
  }

  // Helper method to calculate time ago
  calculateTimeAgo(dateString) {
    if (!dateString) return "Recently";
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        return "1 day ago";
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
      } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return months === 1 ? "1 month ago" : `${months} months ago`;
      } else {
        const years = Math.floor(diffDays / 365);
        return years === 1 ? "1 year ago" : `${years} years ago`;
      }
    } catch (error) {
      console.error('Error calculating time ago:', error);
      return "Recently";
    }
  }

  // Get fallback data for when API fails
  getFallbackTeamActivityData() {
    return [
      {
        id: "fallback-1",
        image: "/png/placeholder.png",
        isVideo: false,
        videoUrl: null,
        tag: "Team Activity",
        title: "Team Activity Updates",
        desc: "Stay tuned for the latest team activity updates.",
        post_desc: "We're working on bringing you the latest team activity updates. Please check back later.",
        overlay: true,
        publishedAt: new Date().toISOString(),
        slug: "team-activity-updates",
        author: "BA Systems Team",
        timeAgo: "Recently"
      }
    ];
  }
}

const teamActivityAPIService = new TeamActivityAPIService();
export default teamActivityAPIService;
